import { client } from "../client";
import type { User } from "../pages/SignInPage";
import type { Paginated, PaginatedFilter } from "../types/axios.types";
import type { Center, CenterStats } from "../types/center.types";
import type { Command } from "../types/command.types";
import type { Patient } from "../types/patient.types";
import type {
	CenterPatientsStats,
	CommandEarmoldTypesStats,
	StatsFilterProps,
} from "../types/stats.types";
import { UserCenterRole } from "../types/user.types";

export const fetchCenters = async (
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
	isArchived?: boolean,
) => {
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	} else {
		params.append("sort", "id");
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	if (isArchived !== undefined && isArchived !== null) {
		params.append("isArchived", isArchived.toString());
	}
	// Query
	const response = await client.get<Paginated<Center[]>>(`/centers`, {
		params,
	});
	return response.data;
};

export const fetchCenter = async (id: number | string | undefined) => {
	if (id === undefined) {
		return null;
	}
	const response = await client.get<Center>(`centers/${id}`);
	return response.data;
};

export const fetchCenterStat = async (id: number | string | undefined) => {
	if (id === undefined) {
		return null;
	}
	const response = await client.get<CenterStats>(`centers/${id}/stats`);
	return response.data;
};

export const fetchCenterUsers = async (
	id: number | string | undefined,
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	// Query
	const response = await client.get<Paginated<User[]>>(`centers/${id}/users`, {
		params,
	});
	return response.data;
};

export const fetchCenterPatients = async (
	id: number | string | undefined,
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	// Query
	const response = await client.get<Paginated<Patient[]>>(
		`centers/${id}/patients`,
		{ params },
	);
	return response.data;
};

export const fetchCenterAudios = async (
	id: number | string | undefined,
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	params.append("role", UserCenterRole.AUDIOPROSTHESIST);
	// Query
	const response = await client.get<Paginated<User[]>>(`centers/${id}/users`, {
		params,
	});
	return response.data;
};

export const fetchCenterCommands = async (
	id: number | string | undefined,
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	if (id === undefined) {
		return null;
	}
	const params = new URLSearchParams();
	// Pagination
	params.append("page", page ? page.toString() : "1");
	params.append("size", size ? size.toString() : "50");
	// Search
	if (search) {
		params.append("search", search);
	}
	// Sort
	if (sortBy) {
		sortBy.forEach((key) => params.append("sort", key));
	}
	// Filters
	if (filters) {
		Object.keys(filters).forEach((key: string) => {
			// Blacklist some keys
			if (
				["page", "size", "search", "sort"].includes(key) ||
				filters[key] === ""
			) {
				return;
			}
			params.append(
				key,
				Array.isArray(filters[key]) ? filters[key].join(",") : filters[key],
			);
		});
	}
	// Query
	const response = await client.get<Paginated<Command[]>>(
		`centers/${id}/commands`,
		{ params },
	);
	return response.data;
};

export const fetchCreationRequestCenters = async () => {
	const response = await client.get<Center[]>(`centers/?activated=f`);
	return response.data;
};

export const fetchCenterCommandsStats = async ({
	id,
}: {
	id: number | string | undefined;
}) => {
	if (id === undefined) {
		return null;
	}
	const response = await client.get(`centers/${id}/stats/commands`);
	return response.data;
};

export const fetchCenterPatientsStats = async ({
	id,
}: {
	id: number | string | undefined;
}) => {
	if (id === undefined) {
		return null;
	}
	const response = await client.get<CenterPatientsStats>(
		`centers/${id}/stats/patients`,
	);
	return response.data;
};

export const fetchCenterCommandsStatusStats = async ({
	id,
	date,
}: { id: number | string | undefined } & StatsFilterProps) => {
	if (id === undefined) {
		return null;
	}
	const params = new URLSearchParams();
	if (date) {
		params.append("date", date.toISOString());
	}
	const response = await client.get<{ [key: string]: number }>(
		`centers/${id}/stats/commands/status`,
		{ params },
	);
	return response.data;
};

export const fetchCenterCommandsEarmoldTypesStats = async ({
	id,
	date,
}: { id: number | string | undefined } & StatsFilterProps) => {
	if (id === undefined) {
		return null;
	}
	const params = new URLSearchParams();
	if (date) {
		params.append("date", date.toISOString());
	}
	const response = await client.get<CommandEarmoldTypesStats[]>(
		`centers/${id}/stats/earmolds/types`,
		{ params },
	);
	return response.data;
};

export const fetchCenterTicketsTypesStats = async ({
	id,
	date,
}: { id: number | string | undefined } & StatsFilterProps) => {
	if (id === undefined) {
		return null;
	}
	const params = new URLSearchParams();
	if (date) {
		params.append("date", date.toISOString());
	}
	const response = await client.get<{ label: string; value: number }[]>(
		`centers/${id}/stats/tickets/types`,
		{ params },
	);
	return response.data;
};
